import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Star, Maximize2, Minimize2, MapPin } from 'lucide-react';
import { Location } from '../types/chat';
import { createPortal } from 'react-dom';
import { panelAnimator } from '../services/panelAnimationService';


interface MapInfoCardProps {
  location: Location | null;
  onClose: () => void;
  markerPosition: { x: number, y: number } | null;
  images: string[];
  onExpandedChange?: (isExpanded: boolean) => void;
  isClosing?: boolean;
  style?: React.CSSProperties;
}

interface GridItem {
  id: string;
  size: 'normal' | 'wide' | 'tall' | 'large';
  imageUrl?: string;
  placeholder?: string;
}

export const MapInfoCard: React.FC<MapInfoCardProps> = ({ 
  location, 
  onClose,
  markerPosition,
  images = [],
  onExpandedChange // Add this 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentImage, setCurrentImage] = useState<string>('');
  const [gridItems, setGridItems] = useState<GridItem[]>([]);
  const cardRef = useRef<HTMLDivElement>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isElevated, setIsElevated] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);

  // Add these state variables after existing states
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [autoPlayProgress, setAutoPlayProgress] = useState(0);
  const autoPlayIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoPlayDuration = 10000; // 6 seconds, make this configurable
  const prevLocationRef = useRef<Location | null>(null);
  const prevIsExpanded = useRef(false);




  // Update it when isExpanded changes
  useEffect(() => {
    prevIsExpanded.current = isExpanded;
  }, [isExpanded]);

  useEffect(() => {
    const handleCollapseEvent = () => {
      if (isExpanded) {
        setIsExpanded(false);
        onExpandedChange?.(false);
      }
    };
    
    window.addEventListener('map-collapse-card', handleCollapseEvent);
    return () => window.removeEventListener('map-collapse-card', handleCollapseEvent);
  }, [isExpanded, onExpandedChange]);

  useEffect(() => {
    // Or check parent containers
    const appContainer = document.querySelector('#root > div');
    console.log('[Z-INDEX DEBUG] App container children:', appContainer?.children);

    const panels = document.querySelectorAll('[class*="panel"], [class*="Panel"]');
    console.log('[Z-INDEX DEBUG] Found panels:', Array.from(panels));
    panels.forEach(el => {
      console.log('Panel:', el.className, 'z-index:', window.getComputedStyle(el).zIndex);
    });
  }, []);

  // Add autoplay effect
  useEffect(() => {
    if (!isAutoPlaying || images.length <= 1 || !isVisible) {
      setAutoPlayProgress(0);
      return;
    }

    const progressInterval = 50; // Update every 50ms
    const progressIncrement = (progressInterval / autoPlayDuration) * 100;

    autoPlayIntervalRef.current = setInterval(() => {
      setAutoPlayProgress(prev => {
        if (prev >= 100) {
          setCurrentImageIndex(current => (current + 1) % images.length);
          return 0;
        }
        return prev + progressIncrement;
      });
    }, progressInterval);

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
    };
  }, [isAutoPlaying, images.length, isVisible, currentImageIndex]);

  // Add this ONE useEffect for panel checking (not two separate ones):
  // Add this effect to listen for logout events
  useEffect(() => {
    const handleLogout = () => {
      // Call the same handleClose function used by the close button
      handleClose();
    };
    
    // Listen for both standard and custom logout events
    window.addEventListener('tripperLogout', handleLogout);
    window.addEventListener('tripper-user-logout', handleLogout);
    window.addEventListener('tripper-clear-map', handleLogout);
    
    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('tripperLogout', handleLogout);
      window.removeEventListener('tripper-user-logout', handleLogout);
      window.removeEventListener('tripper-clear-map', handleLogout);
    };
  }, [onClose]);

  // Set initial image when images array changes
  useEffect(() => {
    if (images.length > 0) {
      setCurrentImage(images[0]);
      setImageLoaded(false);
      setCurrentImageIndex(0);
    }
  }, [images]);

  // Also ensure image loads when location changes
  useEffect(() => {
    if (location && images.length > 0) {
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => {
        setCurrentImage('/placeholder-image.png');
        setImageLoaded(true);
      };
      img.src = images[0];
    }
  }, [location, images]);

  useEffect(() => {
    if (markerPosition && !hasInitialized) {
      setHasInitialized(true);
    }
  }, [markerPosition]);

  // Handle image index changes
  useEffect(() => {
    if (images.length > 0 && currentImageIndex < images.length) {
      setImageLoaded(false);
      setCurrentImage(images[currentImageIndex]);
    }
  }, [currentImageIndex, images]);

  // Show/hide animation
  useEffect(() => {
    if (location && markerPosition && markerPosition.x >= 0 && markerPosition.y >= 0) {
      // Valid position - show with animation
      //setIsVisible(false); // Hide first
      setIsClosing(false);
      setCurrentImageIndex(0);
      setIsExpanded(false);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // Invalid state - hide
      setIsVisible(false);
      setIsExpanded(false);
    }
  }, [location, markerPosition]);


  // Generate grid items
  useEffect(() => {
    if (isExpanded) {
      //generateGridItems();
    }
  }, [isExpanded, location]);

  // Add effect to lower z-index after collapse
  useEffect(() => {
    if (!isExpanded && isElevated) {
      const timer = setTimeout(() => {
        setIsElevated(false);
      }, 600); // After animation completes
      return () => clearTimeout(timer);
    }
  }, [isExpanded, isElevated]);

  // Add this effect to reset position when location changes
  useEffect(() => {
    if (location && markerPosition && prevLocationRef.current?.id !== location.id) {
      // Location changed - ensure we start at the new position
      //setIsVisible(false);
      /* setTimeout(() => {
        setIsVisible(true);
      }, 50); */
    }
    prevLocationRef.current = location;
  }, [location?.id, markerPosition]);
  

  const generateGridItems = async () => {
    
    const items: GridItem[] = [];
    const totalItems = 50;
    
    // Placeholder implementation - replace with actual photo URLs
    for (let i = 0; i < totalItems; i++) {
      const random = Math.random();
      let size: 'normal' | 'wide' | 'tall' | 'large';
      
      if (random < 0.5) size = 'normal';
      else if (random < 0.7) size = 'wide';
      else if (random < 0.9) size = 'tall';
      else size = 'large';
      
      items.push({
        id: `grid-item-${i}`,
        size,
        // Replace with actual Google photo URLs
        imageUrl: `https://picsum.photos/${size === 'wide' ? 200 : 100}/${size === 'tall' ? 200 : 100}?random=${i}`,
        placeholder: '#374151'
      });
    }
    
    setGridItems(items);
  };

  const handleClose_UNUSED = () => {
    setIsExpanded(false); // Collapse first
    setTimeout(() => {
      setIsClosing(true);
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, 600); // Wait for collapse animation
  };

  const handleClose = () => {
    if (isExpanded) {
      setIsExpanded(false);
      setTimeout(() => {
        setIsClosing(true);
        setIsVisible(false);
        setTimeout(onClose, 150);
      }, 600); // Wait for collapse animation
    } else {
      setIsClosing(true);
      setIsVisible(false);
      setTimeout(onClose, 150);
    }
  };

  const toggleExpand = () => {
    const newExpanded = !isExpanded;
    console.log('[MapInfoCard] Toggling expand:', newExpanded);

    if (newExpanded && cardRef.current && markerPosition) {
      setIsElevated(true); // Elevate immediately on expand
      // Calculate the transform needed to center the card
      const rect = cardRef.current.getBoundingClientRect();
      const windowCenterX = window.innerWidth / 2;
      const windowCenterY = window.innerHeight / 2;
      const currentCenterX = rect.left + rect.width / 2;
      const currentCenterY = rect.top + rect.height / 2;
      
      // Store the offsets needed to center
      const xOffset = windowCenterX - currentCenterX;
      const yOffset = windowCenterY - currentCenterY + (rect.height * 0.2); // Adjust for -120% transform
      
      cardRef.current.style.setProperty('--expand-x', `${xOffset}px`);
      cardRef.current.style.setProperty('--expand-y', `${yOffset}px`);
      setIsExpanded(true); // ADD THIS
    } else {
    // When collapsing, don't lower immediately
      setIsExpanded(false);
      // isElevated will be set to false in the useEffect after animation
    }
    setIsExpanded(newExpanded);
    if (onExpandedChange) {
      console.log('[MapInfoCard] Toggling expand:', newExpanded);
      onExpandedChange(newExpanded);
    }
  };

  const handleNextImage_OG = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (images.length > 1) {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAutoPlaying(false);
    setAutoPlayProgress(0);
    if (images.length > 1) {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }
    // Resume after 3 seconds
    setTimeout(() => setIsAutoPlaying(true), 3000);
  };

  const handlePrevImage_OG = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (images.length > 1) {
      setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
    }
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAutoPlaying(false);
    setAutoPlayProgress(0);
    if (images.length > 1) {
      setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
    }
    // Resume after 3 seconds
    setTimeout(() => setIsAutoPlaying(true), 3000);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Don't render if position is invalid OR if map might be moving
  if (!location || !markerPosition || 
      markerPosition.x <= 0 || markerPosition.y <= 0 ||
      markerPosition.x > window.innerWidth || markerPosition.y > window.innerHeight) {
    //console.log('[MAP INFO CONSOLE] Not rendering - invalid position');
    return null;
  }

  const displayName = location.name.split(',')[0].trim();
  const hasImages = images && images.length > 0;


  const expandedContent = (
    <>
      {/* Lightbox overlay */}
      <div 
        className="fixed inset-0"
        style={{
          background: 'rgba(0, 0, 0, 0.9)',
          opacity: isExpanded ? 1 : 0,
          pointerEvents: isExpanded ? 'auto' : 'none',
          transition: isExpanded 
          ? 'opacity 0.4s ease 0.5s' // Add 0.2s delay when expanding
          : 'opacity 0.3s ease', // No delay when closing
          zIndex: isElevated ? 9998 : 48,
        }}
        onClick={toggleExpand}
      />
      
      <div 
        ref={cardRef}
        className="absolute pointer-events-auto"
        style={{ 
          left: isExpanded ? '50%' : markerPosition ? `${markerPosition.x}px` : '50%',
          top: isExpanded ? '50%' : markerPosition ? `${markerPosition.y}px` : '50%',
          transform: isExpanded ? 'translate(-50%, -50%)' : 'translate(-50%, -120%)',
          width: isExpanded ? 'min(90vw, 1400px)' : 'var(--map-card-width, 320px)',
          height: isExpanded ? 'min(90vh, 900px)' : 'var(--map-card-height, 400px)',
          //opacity: isVisible ? 1 : 0,
          opacity: isVisible && !isClosing ? 1 : 0,  // Add isClosing check

            
          transition: hasInitialized 
            ? isExpanded || prevIsExpanded.current  // Add tracking for expand state
              ? 'all 0.6s var(--map-card-transition-smooth)'  // Use 'all' when expanding/collapsing
              : 'width 0.6s var(--map-card-transition-smooth), height 0.6s var(--map-card-transition-smooth), opacity 0.6s var(--map-card-transition-smooth), transform 0.6s var(--map-card-transition-smooth)'
            : 'none',
          visibility: isVisible && markerPosition && !isClosing && !shouldHide ? 'visible' : 'hidden', // Add this
          pointerEvents: isClosing ? 'none' : 'auto',
          zIndex: isElevated ? 9999 : 4000,
          boxShadow: 'var(--map-card-shadow)'
        }}
      >
        {/* Card Content */}
        <div 
          className={`overflow-hidden`}
          onClick={(e) => e.stopPropagation()} // Add this to prevent closing
          style={{
            backgroundColor: 'var(--map-card-bg, #111827)',
            borderRadius: 'var(--map-card-border-radius, 1rem)',
            width: '100%',
            height: '100%',
            transition: hasInitialized 
          ? 'all 0.4s var(--map-card-transition-smooth)'
          : 'none'

          }}
        >
          <div 
            className="relative"
            style={{ 
              height: '100%',
              gridColumn: isExpanded ? '1' : 'auto'
            }}
          >
            {/* Loading placeholder */}
            <div 
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                imageLoaded ? 'opacity-0' : 'opacity-100'
              }`}
              style={{ backgroundColor: 'var(--map-card-loading-bg, #1f2937)' }}
            >
            <div 
                className="rounded-full animate-spin"
                style={{ 
                  width: 'var(--map-card-spinner-size, 24px)',
                  height: 'var(--map-card-spinner-size, 24px)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  borderTopColor: 'white'
                }}
              />
            </div>

            {/* Background Image */}
            {hasImages ? (
              <img 
                src={currentImage} 
                alt={displayName} 
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={handleImageLoad}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder-image.png';
                  setImageLoaded(true);
                }}
              />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--map-card-no-image-bg, #1f2937)' }}
              >
                <span style={{ color: 'var(--map-card-no-image-text, #6b7280)' }}>
                  No image available
                </span>
              </div>
             )}

            {/* Gradient Overlay */}
            <div 
              className="absolute inset-0"
              style={{ 
                background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.2), transparent)',
                transition: 'opacity 0.3s ease',
                bottom: '-2px', // Extend 1px below to cover any subpixel gaps
                height: 'calc(100% + 2px)' // Ensure it covers beyond container
              }}
            />

            {/* Rating Badge */}
            <div 
              className="absolute flex items-center rounded-full backdrop-blur-sm"
              style={{
                top: isExpanded ? '24px' : 'var(--map-card-badge-top, 1rem)',
                left: isExpanded ? '24px' : 'var(--map-card-badge-left, 1rem)',
                padding: isExpanded ? '8px 16px' : 'var(--map-card-badge-padding, 0.375rem 0.75rem)',
                backgroundColor: 'var(--map-card-badge-bg, rgba(0,0,0,0.6))',
                gap: 'var(--map-card-badge-gap, 0.25rem)',
                transition: hasInitialized 
          ? 'all 0.4s var(--map-card-transition-smooth)'
          : 'none'
              }}
            >
              <Star 
                className="fill-current"
                style={{ 
                  width: 'var(--map-card-star-size, 14px)',
                  height: 'var(--map-card-star-size, 14px)',
                  color: 'var(--map-card-star-color, #fbbf24)'
                }}
              />
              <span 
                className="font-semibold"
                style={{ 
                  color: 'var(--map-card-rating-color, white)',
                  fontSize: 'var(--map-card-rating-size, 0.875rem)'
                }}
              >
                {location.rating.toFixed(1)}
              </span>
            </div>

            {/* Expand Button */}
            <button
              onClick={toggleExpand}
              className="absolute backdrop-blur-sm rounded-full transition-all"
              style={{
                top: isExpanded ? '70px' : 'var(--map-card-expand-top, 1rem)',
                right: isExpanded ? '24px' : 'var(--map-card-expand-right, 3.5rem)',
                padding: 'var(--map-card-expand-padding, 0.5rem)',
                backgroundColor: 'var(--map-card-expand-bg, rgba(0,0,0,0.4))',
                width: '36px',
                height: '36px',
                opacity: 1,
                visibility: 'visible',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: hasInitialized 
                ? 'all var(--map-card-transition-interval) var(--map-card-transition-smooth)'
                : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--map-card-expand-hover-bg, rgba(0,0,0,0.6))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--map-card-expand-bg, rgba(0,0,0,0.4))';
              }}
            >
              {isExpanded ? (
                <Minimize2 
                  style={{ 
                    width: 'var(--map-card-expand-icon-size, 18px)',
                    height: 'var(--map-card-expand-icon-size, 18px)',
                    color: 'var(--map-card-expand-icon-color, white)'
                  }}
                />
              ) : (
                <Maximize2 
                  style={{ 
                    width: 'var(--map-card-expand-icon-size, 18px)',
                    height: 'var(--map-card-expand-icon-size, 18px)',
                    color: 'var(--map-card-expand-icon-color, white)'
                  }}
                />
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute backdrop-blur-sm rounded-full transition-colors"
              style={{
                top: isExpanded ? '24px' : 'var(--map-card-close-top, 1rem)',
                right: isExpanded ? '24px' : 'var(--map-card-close-right, 1rem)',
                padding: 'var(--map-card-expand-padding, 0.5rem)',
                backgroundColor: 'var(--map-card-expand-bg, rgba(0,0,0,0.4))',
                width: '36px',
                height: '36px',
                opacity: 1,
                visibility: 'visible',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--map-card-close-hover-bg, rgba(0,0,0,0.6))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--map-card-close-bg, rgba(0,0,0,0.4))';
              }}
            >
              <X 
                style={{ 
                  width: isExpanded ? '28px' : 'var(--map-card-close-icon-size, 18px)',
                  height: isExpanded ? '28px' : 'var(--map-card-close-icon-size, 18px)',
                  color: 'var(--map-card-close-icon-color, white)'
                }}
              />
            </button>
            {isExpanded && images.length > 1 && (
              <div 
                className="absolute left-1/2 transform -translate-x-1/2 h-1 bg-white/20 rounded-full overflow-hidden"
                style={{
                  top: '32px',
                  width: '200px',
                  zIndex: 20
                }}
              >
                <div 
                  className="h-full bg-white/80 transition-transform duration-100 ease-linear rounded-full"
                  style={{ 
                    transform: `translateX(-${100 - autoPlayProgress}%)`,
                    width: '100%'
                  }}
                />
              </div>
            )}
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button 
                  onClick={handlePrevImage}
                  className="absolute top-1/2 transform -translate-y-1/2 transition-all"
                  style={{
                    left: isExpanded ? '32px' : 'var(--map-card-nav-margin, 1rem)',
                    color: 'var(--map-card-nav-color, rgba(255,255,255,0.8))',
                    width: isExpanded ? '48px' : '32px',
                    height: isExpanded ? '48px' : '32px',
                    background: isExpanded ? 'rgba(0,0,0,0.5)' : 'none',
                    backdropFilter: isExpanded ? 'blur(10px)' : 'none',
                    borderRadius: isExpanded ? '50%' : '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: hasInitialized 
          ? 'all var(--map-card-transition-interval) var(--map-card-transition-smooth)'
          : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--map-card-nav-hover-color, white)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--map-card-nav-color, rgba(255,255,255,0.8))';
                  }}
                >
                  <ChevronLeft 
                    strokeWidth={1.5}
                    style={{ 
                      width: 'var(--map-card-nav-size, 32px)',
                      height: 'var(--map-card-nav-size, 32px)'
                    }}
                  />
                </button>
                <button 
                  onClick={handleNextImage}
                  className="absolute top-1/2 transform -translate-y-1/2 transition-all"
                  style={{
                    right: isExpanded ? '32px' : 'var(--map-card-nav-margin, 1rem)',
                    color: 'var(--map-card-nav-color, rgba(255,255,255,0.8))',
                    width: isExpanded ? '48px' : '32px',
                    height: isExpanded ? '48px' : '32px',
                    background: isExpanded ? 'rgba(0,0,0,0.5)' : 'none',
                    backdropFilter: isExpanded ? 'blur(10px)' : 'none',
                    borderRadius: isExpanded ? '50%' : '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: hasInitialized 
          ? 'all 0.4s var(--map-card-transition-smooth)'
          : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--map-card-nav-hover-color, white)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--map-card-nav-color, rgba(255,255,255,0.8))';
                  }}
                >
                  <ChevronRight 
                    strokeWidth={1.5}
                    style={{ 
                      width: 'var(--map-card-nav-size, 32px)',
                      height: 'var(--map-card-nav-size, 32px)'
                    }}
                  />
                </button>
                {/* Autoplay progress for expanded view */}
                {/* Autoplay progress for expanded view */}
              </>
            )}

            {/* Content Overlay */}
            <div 
              className="absolute bottom-0 left-0 right-0"
              style={{ 
                padding: isExpanded ? '32px' : 'var(--map-card-content-padding, 1.5rem)',
                maxWidth: isExpanded ? '600px' : 'none',
                transition: 'all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
              }}
            >
              <h3 
                className="font-bold"
                style={{ 
                  color: 'var(--map-card-title-color, white)',
                  fontSize: isExpanded ? '32px' : 'var(--map-card-title-size, 1.5rem)',
                  marginBottom: 'var(--map-card-title-margin, 0.5rem)',
                  transition: 'font-size 0.3s ease'
                }}
              >
                {displayName}
              </h3>
              {location.city && location.country && (
                <p 
                  className="flex items-center gap-1"
                  style={{ 
                    color: 'var(--map-card-location-color, rgba(255,255,255,0.8))',
                    fontSize: 'var(--map-card-location-size, 0.875rem)',
                    marginBottom: 'var(--map-card-location-margin, 0.75rem)'
                  }}
                >
                  <MapPin size={14} />
                  {location.city}, {location.country}
                </p>
              )}
              {location.description && location.description !== 'No description' && (
                <p 
                  className={isExpanded ? '' : 'line-clamp-2'}
                  style={{ 
                    color: 'var(--map-card-description-color, rgba(255,255,255,0.7))',
                    fontSize: 'var(--map-card-description-size, 0.875rem)',
                    marginTop: 'var(--map-card-description-margin-top, 0.5rem)',
                    transform: 'translateY(var(--map-card-description-offset-y, 0px))',
                    WebkitLineClamp: isExpanded ? 'unset' : 2,
                    transition: '-webkit-line-clamp 0.3s ease'
                  }}
                >
                  {location.description}
                </p>
              )}
            </div>

            {/* Image Indicators */}
            {images.length > 1 && (
              <div 
                className="absolute left-1/2 transform -translate-x-1/2 flex items-center"
                style={{ 
                  bottom: isExpanded ? '32px' : 'var(--map-card-indicators-bottom, 1rem)',
                  gap: 'var(--map-card-indicators-gap, 0.375rem)',
                  transition: 'all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                }}
              >
                {images.map((_, index) => (
                  <div
                    key={index}
                    className="relative"
                    style={{
                      width: index === currentImageIndex ? '24px' : '6px',
                      height: '6px',
                    }}
                  >
                    <div 
                      className="absolute"
                      style={{
                        height: '6px',
                        width: index === currentImageIndex ? '24px' : '6px',
                        backgroundColor: index === currentImageIndex 
                          ? 'var(--map-card-indicator-active-color, white)' 
                          : 'var(--map-card-indicator-color, rgba(255,255,255,0.4))',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease-out',
                        left: '50%',
                        transform: 'translateX(-50%)', // Center it
                        top: 0
                      }}
                    />
                  </div>
                ))}
              </div>
              
            )}
            {/* ADD AUTOPLAY PROGRESS BAR HERE */}
            {images.length > 1 && !isExpanded && (
              <div 
                className="absolute left-0 right-0 h-1 bg-white/20"
                style={{ 
                  bottom: 0,
                  overflow: 'hidden'
                }}
              >
                <div 
                  className="h-full bg-white transition-transform duration-100 ease-linear"
                  style={{ 
                    transform: `translateX(-${100 - autoPlayProgress}%)`,
                    width: '100%'
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Triangle Pointer (only when not expanded) */}
        {!isExpanded && (
          <div 
            className="rotate-45 absolute left-1/2 transform -translate-x-1/2"
            style={{ 
              width: 'var(--map-card-pointer-size, 16px)',
              height: 'var(--map-card-pointer-size, 16px)',
              backgroundColor: 'var(--map-card-bg, #111827)',
              bottom: 'calc(var(--map-card-pointer-size, 16px) / -2)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}
          />
        )}
      </div>
    </>
  );

  return createPortal(expandedContent, document.body);
};